import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { useQueryParamsEffect, useQueryParamTerm } from 'pricing-pages-ui/utils/urls/queryParamUtils';
import Screens from '../../constants/routing/Screens';
import { usePageParams } from '../../hooks/usePageParams';
import { useAnchorLinkScroll } from '../../hooks/htmlElementHooks';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useTrackRetail } from '../../hooks/useTrackRetail';
import { ProductCalculator } from '../calculator/ProductCalculator';
import ValuePropsSection from '../common/ValuePropsSection';
import { getHighlightsForProductLine, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { useFeatureStore } from '../../hooks/useFeatureStore';
import CodeSplitEnvWrapper from '../common/env/CodeSplitEnvWrapper';
import { getMerchCardsToShow } from '../../constants/merchCards/MerchCardsToShow';
import IntegrationsSection from '../common/Integrations';
import { useBusinessObjectives } from '../../hooks/useBusinessObjectives';
import MobileWrapperWithTabs from './containers/MobileWrapperWithTabs';
import { NavItemEnum } from '../../constants/routing/NavItems';
import MobileViewAllFeatures from './components/MobileViewAllFeatures';
import MobileProductLevelSelectorButtons from './MobileProductLevelSelectorButtons';
import MobileHighlightedFreeCard from './MobileHighlightedFreeCard';
import MobileHighlightedMerchCard from './components/MobileHighlightedMerchCard';
import { borderStyle, StyledCardGrid } from '../merch-card/MerchCardSharedComponents';
import { useSelector } from 'react-redux';
import StarterCrmSuiteCtaCardGWP from '../product-pages/StarterCrmSuiteCtaCardGWP';
import { ENTERPRISE, tabIdToProductLevels } from '../../constants/routing/Tabs';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { CMS } from 'self-service-api/constants/ProductLines';
import { isStarterGWP2023EligibleSelector } from '../../selectors/PromotionSelectors';

const MobileProductPage = ({
  productLine
}) => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle(productLine.toLowerCase());
  const isStarterGWP = useSelector(isStarterGWP2023EligibleSelector);
  const {
    tab
  } = usePageParams();
  const [selectedTab, setSelectedTab] = useState(tab);
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: Screens[productLine],
      tab
    });
    setSelectedTab(tab);
  }, [productLine, tab]);
  const screen = productLine.toLowerCase();
  const highlightedProducts = getHighlightsForProductLine(productLine);
  const productLevels = getMerchCardsToShow({
    tab
  });
  const filteredHighlightedProducts = highlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const [selectedProductLevel, setSelectedProductLevel] = useState(productLevels[0]);
  useFeatureStore({
    highlightedProducts: filteredHighlightedProducts,
    productLine
  });
  useBusinessObjectives({
    highlightedProducts: filteredHighlightedProducts
  });
  const commonStyles = {
    gridColumnStart: 1,
    paddingLeft: 28,
    paddingRight: 28,
    borderRight: borderStyle,
    borderLeft: borderStyle
  };

  const onTabChange = newTab => {
    setSelectedTab(newTab);
    setSelectedProductLevel(tabIdToProductLevels[newTab][0]);
  };

  const disclaimer = selectedProductLevel === STARTER && isStarterGWP && productLine !== CMS ? 'pricingPage.promotions.starterGWPDisclaimer' : undefined;
  return /*#__PURE__*/_jsxs(MobileWrapperWithTabs, {
    navMarkerName: `${productLine}_PAGE`,
    productLine: productLine,
    navItem: NavItemEnum[productLine],
    highlightedProductsValue: filteredHighlightedProducts,
    screen: screen,
    productLevels: productLevels,
    tab: selectedTab,
    setTab: onTabChange,
    children: [isStarterGWP && selectedTab !== ENTERPRISE && /*#__PURE__*/_jsx(StarterCrmSuiteCtaCardGWP, {
      containerStyle: {
        marginBottom: '24px'
      }
    }), /*#__PURE__*/_jsx(MobileProductLevelSelectorButtons, {
      productLevels: productLevels,
      onClick: setSelectedProductLevel,
      selectedProductLevel: selectedProductLevel
    }), /*#__PURE__*/_jsx("div", {
      className: "flex-column align-center justify-center",
      children: /*#__PURE__*/_jsx(StyledCardGrid, {
        columns: 1,
        children: selectedProductLevel === 'FREE' ? /*#__PURE__*/_jsx(MobileHighlightedFreeCard, {
          productLine: productLine,
          commonStyles: commonStyles,
          perMonth: false
        }) : /*#__PURE__*/_jsx(MobileHighlightedMerchCard, {
          productLevel: selectedProductLevel,
          productLine: productLine,
          selectedTab: selectedTab,
          commonStyles: commonStyles,
          disclaimer: disclaimer
        })
      })
    }), /*#__PURE__*/_jsx(MobileViewAllFeatures, {
      productLine: productLine,
      productLevel: selectedProductLevel,
      trackingSource: Screens[productLine]
    }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(ProductCalculator, {
      productLine: productLine,
      screen: Screens[productLine]
    }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
      publicComponentLoader: () => import(
      /* webpackChunkName: "public-chunk" */
      `pricing-pages-ui/components/feature-modal/FeatureCardWrapper`),
      publicComponentProps: {
        productLine
      }
    }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
      publicComponentLoader: () => import(
      /* webpackChunkName: "public-chunk" */
      `pricing-pages-ui/components/company-proof/CompanyProof`),
      publicComponentProps: {
        tab: selectedTab || tab
      }
    }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
      publicComponentLoader: () => import(
      /* webpackChunkName: "public-chunk" */
      `pricing-pages-ui/components/case-studies/CaseStudies`),
      publicComponentProps: {
        productLine,
        tab: selectedTab
      }
    }), /*#__PURE__*/_jsx("div", {
      className: "align-self-start flex-column",
      style: {
        maxWidth: 1000,
        overflow: 'auto'
      },
      children: /*#__PURE__*/_jsx(ValuePropsSection, {})
    })]
  });
};

export default MobileProductPage;